import useRolePermission from "entities/auth/hooks/useRolePermission";
import { invalidateEntireModuleListQuery } from "entities/modules/queries/useModuleListByTagIdQuery";
import { useDeletePageModuleMutation } from "entities/pages/mutations/useDeletePageModuleMutation";
import { useReorderPageModulesMutation } from "entities/pages/mutations/useReorderPageModulesMutation";
import { PageModuleData } from "entities/pages/types";
import { ModulesView, PagesTabs } from "modules/brands/context/types";
import { FC, useState, useCallback } from "react";
import DeleteConfirmationModal from "shared/components/DeleteConfirmationModal";
import DragNDropTable from "shared/components/DragNDropTable";
import DragNDropTileTable from "shared/components/DragNDropTileTable";
import InfiniteScrollLoader from "shared/components/InfiniteScrollLoader";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import FlexColumn from "shared/components/Layout/components/FlexColumn";
import SpaceBetweenCenter from "shared/components/Layout/components/SpaceBetweenCenter";
import PageHeading from "shared/components/PageHeading";
import PrimaryButton from "shared/components/PrimaryButton";
import SearchField from "shared/components/SearchField";
import Snackbar from "shared/components/Snackbar";
import Switcher from "shared/components/Switcher";
import { useModalState } from "shared/hooks/useModalState";
import { ListIcon } from "shared/theme/icons/ListIcon";
import { TilesIcon } from "shared/theme/icons/TilesIcon";
import GenerateAIModule from "./components/GenerateAIModule";
import ModuleDetailsDrawerContainer from "./components/ModuleDetailsDrawer";
import ModuleScheduleView from "./components/ModuleScheduleView";
import MoveOrDuplicateModuleModal from "./components/MoveOrDuplicateModuleModal";
import PasteModulePopup from "./components/PasteModulePopup";
import { TOTAL_ANIMATION_DURATION } from "./constants";
import { PageModulesProvider, usePageModules } from "./context";
import { getPageModulesColumns, getTextForDeletingPopup } from "./helpers";
import { ColumnsProps, MoveOrDuplicateType } from "./types";

const PagesModulesContainer: FC = () => (
  <PageModulesProvider>
    <PageModules />
  </PageModulesProvider>
);

const PageModules: FC = () => {
  const pasteModal = useModalState();
  const [deletableModule, setDeletableModule] = useState<PageModuleData | null>(
    null,
  );
  const deleteModalState = useModalState();
  const { mutate: mutateDeleteModule, isPending: isDeletingModule } =
    useDeletePageModuleMutation();

  const {
    isLoadingPages,
    isRefetchingPages,
    modulePageTitle,
    moduleDetailsDrawer,
    onUpdateStatus,
    brandId,
    pageId,
    searchData,
    loadMoreModules,
    hasNextPage,
    isFetchingNextPage,
    data: allModules,
    activeTab,
    setActiveTab,
    moveOrDuplicateModalState,
    setMoveOrDuplicateType,
    setMoveOrDuplicateModule,
    alertOpen,
    setAlertOpen,
    alertIsSuccess,
    alertMessage,
    activeView,
    setActiveView,
  } = usePageModules();

  const isPreviewTab = activeTab === PagesTabs.Preview;
  const isListView = activeView === ModulesView.List;
  const isTilesView = activeView === ModulesView.Tiles;
  const writeAccess = useRolePermission("brandsAndPages:write");
  const { mutate: mutateReorder, isPending: isPendingReorder } =
    useReorderPageModulesMutation();

  const [newlyGeneratedModuleId, setNewlyGeneratedModuleId] =
    useState<string>();

  const handleModuleGenerated = (moduleId: string) => {
    if (isRefetchingPages || isLoadingPages) return;

    setNewlyGeneratedModuleId(moduleId);
    setTimeout(() => {
      setNewlyGeneratedModuleId(undefined);
    }, TOTAL_ANIMATION_DURATION);
  };

  const handleDeleteModuleClick = (moduleData: PageModuleData) => {
    setDeletableModule(moduleData);
    deleteModalState.openModal();
  };

  const handleDeleteModuleConfirm = () => {
    if (!deletableModule) {
      return;
    }

    mutateDeleteModule({
      brandId,
      pageId,
      moduleId: deletableModule.id,
      onSuccess: deleteModalState.closeModal,
    });
    invalidateEntireModuleListQuery();
  };

  const handleMoveModuleClick = (moduleData: PageModuleData) => {
    setMoveOrDuplicateModule(moduleData);
    setMoveOrDuplicateType(MoveOrDuplicateType.Move);
    moveOrDuplicateModalState.openModal();
  };

  const handleDuplicateModuleClick = (moduleData: PageModuleData) => {
    setMoveOrDuplicateModule(moduleData);
    setMoveOrDuplicateType(MoveOrDuplicateType.Duplicate);
    moveOrDuplicateModalState.openModal();
  };

  const columnsParams: ColumnsProps = {
    moduleDetailsDrawer: moduleDetailsDrawer,
    handleStatus: onUpdateStatus,
    onDeleteClick: handleDeleteModuleClick,
    onMoveClick: handleMoveModuleClick,
    onDuplicateClick: handleDuplicateModuleClick,
    readOnly: !writeAccess.allowed,
  };

  const columns = getPageModulesColumns(columnsParams);

  const handleReorder = useCallback(
    (reorderedVisibleModules: PageModuleData[]) => {
      if (searchData.isSearchActive) {
        return;
      }

      const reorderedModulesMap = new Map(
        reorderedVisibleModules.map((module, idx) => [
          module.id,
          { ...module, order: idx + 1 },
        ]),
      );

      const allReorderedModules = allModules.map((module) => {
        const reorderedModule = reorderedModulesMap.get(module.id);
        if (reorderedModule) {
          return reorderedModule;
        }
        return module;
      });

      const sortedAllReorderedModules = [...allReorderedModules].sort(
        (a, b) => a.order - b.order,
      );

      const formattedModules = sortedAllReorderedModules.map((module, idx) => ({
        ...module,
        order: idx + 1,
      }));

      mutateReorder({
        brandId,
        pageId,
        modules: formattedModules,
      });
    },
    [allModules, brandId, mutateReorder, pageId, searchData.isSearchActive],
  );

  return (
    <FlexColumn>
      <PageHeading customPageTitle={modulePageTitle}>
        {writeAccess.allowed && (
          <FlexCenter gap="1rem" alignItems="baseline">
            <GenerateAIModule onModuleGenerated={handleModuleGenerated} />
            <FlexCenter gap="1rem">
              <PrimaryButton
                title="Insert Module"
                size="small"
                color="secondary"
                variant="outlined"
                rounded={false}
                onClick={pasteModal.openModal}
              />
              <PrimaryButton
                title="Add Module"
                onClick={() => moduleDetailsDrawer.openDrawer()}
                size="small"
              />
            </FlexCenter>
          </FlexCenter>
        )}
      </PageHeading>

      <FlexCenter
        marginBottom={"1rem"}
        gap="1rem"
        flexDirection="column"
        alignItems="flex-start"
      >
        <Switcher
          options={[
            { id: PagesTabs.Preview, label: "Page Preview" },
            { id: PagesTabs.Schedule, label: "Schedule" },
          ]}
          activeOptionId={activeTab}
          onChange={(optionId) => setActiveTab(optionId as PagesTabs)}
        />
      </FlexCenter>

      {isPreviewTab ? (
        <>
          <SpaceBetweenCenter marginBottom={"1rem"}>
            <SearchField
              placeholder="Search..."
              value={searchData.value}
              onChange={searchData.onChange}
            />
            <Switcher
              options={[
                {
                  id: ModulesView.List,
                  label: "List",
                  icon: <ListIcon size={16} />,
                },
                {
                  id: ModulesView.Tiles,
                  label: "Tiles",
                  icon: <TilesIcon size={16} />,
                },
              ]}
              labelVisible={false}
              activeOptionId={activeView}
              onChange={(optionId) => setActiveView(optionId as ModulesView)}
            />
          </SpaceBetweenCenter>

          {isListView && (
            <>
              <DragNDropTable<PageModuleData>
                items={searchData.filteredData || []}
                columns={columns}
                reorderDisabled={
                  !writeAccess.allowed || searchData.isSearchActive
                }
                isItemsFetched={!isLoadingPages}
                isItemsLoading={isLoadingPages || isPendingReorder}
                noItemsMessage="No modules found"
                emptyListMessage="No modules found"
                onItemsReorder={handleReorder}
                onRowClick={(params) => {
                  moduleDetailsDrawer.openDrawer(params);
                }}
              />

              <InfiniteScrollLoader
                onIntersect={loadMoreModules}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                disabled={searchData.isSearchActive}
              />
            </>
          )}

          {isTilesView && (
            <>
              <DragNDropTileTable<PageModuleData>
                items={searchData.filteredData || []}
                reorderDisabled={
                  !writeAccess.allowed || searchData.isSearchActive
                }
                isItemsFetched={!isLoadingPages && !isRefetchingPages}
                isItemsLoading={isLoadingPages || isPendingReorder}
                noItemsMessage="No modules found"
                emptyListMessage="No modules found"
                onItemsReorder={handleReorder}
                onRowClick={(params) => {
                  moduleDetailsDrawer.openDrawer(params);
                }}
                handleDeleteModuleClick={handleDeleteModuleClick}
                newlyGeneratedModuleId={newlyGeneratedModuleId}
              />

              <InfiniteScrollLoader
                onIntersect={loadMoreModules}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                disabled={searchData.isSearchActive}
              />
            </>
          )}
        </>
      ) : (
        <ModuleScheduleView
          modules={allModules}
          isLoading={isLoadingPages}
          moduleDetailsDrawer={moduleDetailsDrawer}
          onUpdateStatus={onUpdateStatus}
          brandId={brandId}
          modulePageTitle={modulePageTitle}
        />
      )}
      <ModuleDetailsDrawerContainer />
      <PasteModulePopup
        open={pasteModal.isOpen}
        onClose={pasteModal.closeModal}
      />
      <DeleteConfirmationModal
        title={`Delete "${deletableModule?.name ?? ""}" Module`}
        content={
          deletableModule ? getTextForDeletingPopup(deletableModule) : ""
        }
        applyButtonTitle="Yes, delete"
        onClose={deleteModalState.closeModal}
        open={deleteModalState.isOpen}
        handleSuccess={handleDeleteModuleConfirm}
        applyButtonLoading={isDeletingModule}
      />
      <MoveOrDuplicateModuleModal
        open={moveOrDuplicateModalState.isOpen}
        onClose={moveOrDuplicateModalState.closeModal}
      />
      <Snackbar
        open={alertOpen}
        success={alertIsSuccess}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />
    </FlexColumn>
  );
};

export default PagesModulesContainer;
